import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLang } from '../context/LangContext';
import './Home.css';

/* ── helpers ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function CountUp({ end, suffix = '' }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useInView();
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = end / 60;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ── SVG Icons ── */
const Icons = {
  Dispatch: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  ),
  Clubbing: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <rect x="2" y="3" width="8" height="8" rx="1"/><rect x="14" y="3" width="8" height="8" rx="1"/>
      <rect x="2" y="13" width="8" height="8" rx="1"/><rect x="14" y="13" width="8" height="8" rx="1"/>
    </svg>
  ),
  Analytics: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  Route: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <circle cx="5" cy="5" r="2"/><circle cx="19" cy="19" r="2"/>
      <path d="M5 7v4a6 6 0 006 6h2"/>
    </svg>
  ),
  Tracking: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 010 16c-4.5 0-8-7-8-8a8 8 0 0116 0z"/>
    </svg>
  ),
  Fraud: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
};

export default function Home() {
  const { t, lang } = useLang();
  const [testimonials, setTestimonials] = useState([]);
  const [heroRef, heroVisible]   = useInView(0.1);
  const [statsRef, statsVisible] = useInView(0.2);
  const [featRef, featVisible]   = useInView(0.1);
  const [screenRef, screenVisible] = useInView(0.1);

  useEffect(() => {
    axios.get('/api/testimonials/public').then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  const FEATURES = [
    { Icon: Icons.Dispatch,  titleKey: 'feat.dispatch.title', descKey: 'feat.dispatch.desc' },
    { Icon: Icons.Clubbing,  titleKey: 'feat.clubbing.title', descKey: 'feat.clubbing.desc' },
    { Icon: Icons.Analytics, titleKey: 'feat.analytics.title', descKey: 'feat.analytics.desc' },
    { Icon: Icons.Route,     titleKey: 'feat.route.title',    descKey: 'feat.route.desc' },
    { Icon: Icons.Tracking,  titleKey: 'feat.tracking.title', descKey: 'feat.tracking.desc' },
    { Icon: Icons.Fraud,     titleKey: 'feat.fraud.title',    descKey: 'feat.fraud.desc' },
  ];

  const STATS = [
    { value: 30, suffix: '%', labelKey: 'stats.costs' },
    { value: 50, suffix: '%', labelKey: 'stats.productivity' },
    { value: 99, suffix: '.9%', labelKey: 'stats.automation' },
    { value: 40, suffix: '%', labelKey: 'stats.faster' },
  ];

  return (
    <main className="home" dir={lang === 'ar' ? 'rtl' : 'ltr'}>

      {/* ── HERO ── */}
      <section className="hero" ref={heroRef}>
        <div className="hero__bg" />
        <div className="container hero__content">
          <p className={`hero__badge${heroVisible ? ' fade-up' : ''}`} style={{ animationDelay: '0.1s' }}>
            {t('hero.badge')}
          </p>
          <h1 className={`hero__title${heroVisible ? ' fade-up' : ''}`} style={{ animationDelay: '0.25s' }}>
            {t('hero.title1')}<br />{t('hero.title2')} <span className="gradient-text">Route Rover</span>
          </h1>
          <p className={`hero__sub${heroVisible ? ' fade-up' : ''}`} style={{ animationDelay: '0.4s' }}>
            {t('hero.sub')}
          </p>
          <div className={`hero__ctas${heroVisible ? ' fade-up' : ''}`} style={{ animationDelay: '0.55s' }}>
            <Link to="/contact" className="cta-primary">{t('hero.cta1')}</Link>
            <Link to="/features" className="cta-secondary">{t('hero.cta2')}</Link>
          </div>
        </div>
        <div className="hero__scroll-hint">↓</div>
      </section>

      {/* ── CLIENTS STRIP ── */}
      <section className="clients">
        <div className="container">
          <p className="clients__label">{t('clients.label')}</p>
          <div className="clients__track">
            <div className="clients__inner">
              {['FastDeliver Co.','QuickMart','SpeedBike','HomeDrop','RapidX','SwiftRoute','FleetPro','DeliverNow'].map((c, i) => (
                <div className="clients__logo" key={i}>{c}</div>
              ))}
              {['FastDeliver Co.','QuickMart','SpeedBike','HomeDrop','RapidX','SwiftRoute','FleetPro','DeliverNow'].map((c, i) => (
                <div className="clients__logo" key={`d${i}`}>{c}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ALTERNATING FEATURE SECTIONS ── */}
      <section className="feat-showcase">
        <div className="container">

          {/* 1 — Dispatch */}
          <div className="feat-row">
            <div className="feat-row__text">
              <h2>{lang === 'ar' ? 'توصيل منزلي أسرع مرتين' : '2X Faster Automated Home Delivery'}</h2>
              <p>{lang === 'ar'
                ? 'سرّع دورة التوصيل بأكملها، من الإرسال الأذكى إلى تحسين أوقات التحضير، حتى يخرج كل طلب أسرع ويصل في الوقت المحدد باستمرار.'
                : 'Speed up your entire delivery cycle, from smarter dispatching to optimizing in-store prep times, so every order goes out faster and hits customer promise times consistently. With every rider\'s productivity maximized, you deliver fresh, on time, every time.'
              }</p>
              <Link to="/contact" className="feat-cta">{lang === 'ar' ? 'ابدأ الآن' : 'Get Started'}</Link>
            </div>
            <div className="feat-row__visual">
              <div className="fv-map">
                <div className="fv-map__grid" />
                <div className="fv-map__delivered">
                  <span className="fv-delivered-badge">
                    <svg viewBox="0 0 16 16" fill="none" width="14" height="14"><circle cx="8" cy="8" r="8" fill="#0057FF"/><path d="M4.5 8l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    {lang === 'ar' ? 'تم التوصيل' : 'Delivered'}
                  </span>
                </div>
                <div className="fv-map__pin">
                  <svg viewBox="0 0 32 32" width="32" height="32"><circle cx="16" cy="16" r="16" fill="#0057FF" opacity="0.15"/><circle cx="16" cy="16" r="10" fill="#0057FF"/><path d="M12 16l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div className="fv-map__growth">
                  <strong>+200%</strong>
                  <div className="fv-sparkline">
                    {[20,35,28,50,42,65,58,80].map((h,i) => (
                      <div key={i} style={{ height: `${h}%`, background: i > 5 ? '#0057FF' : '#C8D8F0' }} />
                    ))}
                  </div>
                </div>
                <div className="fv-map__rider">
                  <div className="fv-rider-avatar">A</div>
                  <div>
                    <strong>{lang === 'ar' ? 'أحمد محمد' : 'Ahmed Mohamed'}</strong>
                    <span>+20 100 123 456</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2 — Order Clubbing */}
          <div className="feat-row feat-row--reverse">
            <div className="feat-row__text">
              <h2>{lang === 'ar' ? '٩٩.٩٪ تجميع تلقائي للطلبات' : '99.9% Automated Order Clubbing'}</h2>
              <p>{lang === 'ar'
                ? 'يتم تجميع الطلبات وإرسالها للسائق المناسب في ثوانٍ — بدون قرارات يدوية أو تأخيرات. الطلبات العاجلة أو عالية القيمة تأخذ الأولوية تلقائياً لحماية الطازجية وتجربة العميل.'
                : 'Orders are auto-grouped and dispatched to the right rider in seconds, no manual decisions, no delays. Hot or high-value orders can automatically take priority to protect freshness and customer experience.'
              }</p>
              <Link to="/contact" className="feat-cta">{lang === 'ar' ? 'ابدأ الآن' : 'Get Started'}</Link>
            </div>
            <div className="feat-row__visual">
              <div className="fv-orders">
                {[
                  { id: '#35', time: '05:20 PM', pickup: '05 min', amount: '420.96', name: lang === 'ar' ? 'إسلام عاطف' : 'Eslam Atef', addr: lang === 'ar' ? 'فيلا ٣٢، شارع ٢٠٥' : 'Villa 32, Street 205' },
                  { id: '#37', time: '05:30 PM', pickup: '08 min', amount: '610.50', name: lang === 'ar' ? 'عبدالله الجمال' : 'Abdullah El Gamal', addr: lang === 'ar' ? 'فيلا ٤١، شارع ١٠٣' : 'Villa 41, Street 103' },
                ].map((o, i) => (
                  <div className="fv-order-card" key={i}>
                    <div className="fv-order-card__header">
                      <div className="fv-order-icon">
                        <svg viewBox="0 0 20 20" fill="none" width="16" height="16"><rect x="2" y="2" width="16" height="16" rx="4" fill="#EEF2FF"/><path d="M6 10h8M6 7h8M6 13h5" stroke="#0057FF" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </div>
                      <strong>{lang === 'ar' ? 'طلب ' : 'Order '}{o.id}</strong>
                      <span>{o.time}</span>
                      <svg viewBox="0 0 20 20" fill="none" width="16" height="16" style={{marginLeft:'auto'}}><path d="M7 10l3 3 3-3" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <div className="fv-order-card__body">
                      <div className="fv-order-detail">
                        <span className="fv-label">{lang === 'ar' ? 'استلام' : 'Pic up'}</span>
                        <span className="fv-time">{o.pickup}</span>
                        <span className="fv-amount">{o.amount} {lang === 'ar' ? 'نقدي' : 'cash'}</span>
                      </div>
                      <div className="fv-order-rider">{o.name} · <em>{o.addr}</em></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 3 — Analytics */}
          <div className="feat-row">
            <div className="feat-row__text">
              <h2>{lang === 'ar' ? 'تحليلات معمّقة' : 'Deep-Dive Analytics'}</h2>
              <p>{lang === 'ar'
                ? 'حلّل كل ثانية في وقت التوصيل — التحضير، الاستلام، الانتظار، التسليم — لتحديد مناطق التحسين. قارن المتاجر والفرق أو الأساطيل الخارجية لمعرفة من يعيقك أو يدفعك للأمام.'
                : 'Break down every second of delivery time, prep, fetching, waiting, drop-off, to spot improvement areas. Compare stores, teams, or third-party fleets to see who\'s pulling you down or pushing you ahead.'
              }</p>
              <Link to="/contact" className="feat-cta">{lang === 'ar' ? 'ابدأ الآن' : 'Get Started'}</Link>
            </div>
            <div className="feat-row__visual">
              <div className="fv-analytics">
                {[
                  { label: lang === 'ar' ? 'وقت التوصيل' : 'Delivery time', pct: 55, color: '#22C55E' },
                  { label: lang === 'ar' ? 'وقت التحضير' : 'Prep time',     pct: 75, color: '#0057FF' },
                  { label: lang === 'ar' ? 'وقت الاستلام' : 'Fetching time', pct: 60, color: '#F59E0B' },
                  { label: lang === 'ar' ? 'وقت الانتظار' : 'Waiting time',  pct: 90, color: '#EF4444' },
                ].map((r, i) => (
                  <div className="fv-bar-row" key={i}>
                    <div className="fv-bar-header">
                      <span>{r.label}</span>
                      <span>{r.pct}%</span>
                    </div>
                    <div className="fv-bar-track">
                      <div className="fv-bar-fill" style={{ width: `${r.pct}%`, background: r.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── FLEET MANAGEMENT SECTION ── */}
      <section className="fleet-section">
        <div className="container">
          <h2 className="fleet-title">
            {lang === 'ar'
              ? <><span>عزّز عملياتك</span> بأدوات إدارة <em>السائقين والأسطول</em></>
              : <><span>Enhance Operations</span> with Courier & Fleet <em>Management Tools</em></>
            }
          </h2>
          <div className="fleet-grid">
            {[
              {
                bold:  lang === 'ar' ? 'تخطيط الوردية' : 'Shift',
                rest:  lang === 'ar' ? ' وحضور الأسطول' : ' Planning & Fleet Attendance',
                desc:  lang === 'ar' ? 'أتمتة الورديات، تحسين التداخل، وتتبع الحضور. تأكد من وجود السائق المناسب في المكان المناسب في الوقت المناسب.' : 'Automate shifts, optimize overlap, and track attendance. Making sure the right riders are in the right place, at the right time.',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41"/></svg>,
              },
              {
                bold:  lang === 'ar' ? 'رواتب تلقائية' : 'Automated',
                rest:  lang === 'ar' ? ' للسائقين' : ' courier payroll',
                desc:  lang === 'ar' ? 'اربط بيانات الرحلات مباشرةً بالمدفوعات بدون نزاعات. عادل وسريع وشفاف.' : 'Connect trip data directly to payments, with zero disputes. Fair, fast, and transparent.',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
              },
              {
                bold:  lang === 'ar' ? 'تتبع أداء' : 'Courier',
                rest:  lang === 'ar' ? ' السائقين' : ' Performance Tracking',
                desc:  lang === 'ar' ? 'تتبع الإنتاجية الفعلية لكل سائق. حدد أبطالك والضعفاء بناءً على بيانات دقيقة عن وقت العمل الفعلي.' : 'Track every rider\'s real productivity. Spot your top players and underperformers with reliable data on actual on-duty time.',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
              },
              {
                bold:  lang === 'ar' ? 'إدارة العهدة' : 'Custody',
                rest:  lang === 'ar' ? ' والمدفوعات النقدية' : ' Management',
                desc:  lang === 'ar' ? 'أدر الدفع النقدي عند التوصيل وحدود العهدة تلقائياً. اجعل السائقين مسؤولين بتتبع العهدة لحظياً.' : 'Manage cash on delivery and custody limits automatically. Hold riders accountable with real-time custody tracking.',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="2" y="6" width="20" height="14" rx="2"/><path d="M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><circle cx="12" cy="13" r="2"/></svg>,
              },
            ].map((f, i) => (
              <div className="fleet-item" key={i}>
                <div className="fleet-item__icon">{f.icon}</div>
                <h3><strong>{f.bold}</strong>{f.rest}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM FEATURES GRID ── */}
      <section className="platform-features">
        <div className="container">
          <p className="section-label">{lang === 'ar' ? 'المنصة الكاملة' : 'Full Platform'}</p>
          <h2 className="section-title">{lang === 'ar' ? 'كل ما تحتاجه في منصة واحدة' : 'Everything You Need in One Platform'}</h2>
          <div className="pf-grid">
            {[
              {
                title: lang === 'ar' ? 'الإرسال التلقائي' : 'Auto Dispatch',
                desc:  lang === 'ar' ? 'إرسال الطلبات تلقائياً' : 'Dispatch orders automatically',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 17.5h7M17.5 14v7"/></svg>,
              },
              {
                title: lang === 'ar' ? 'التتبع المباشر' : 'Live Tracking',
                desc:  lang === 'ar' ? 'تتبع أسطولك لحظياً' : 'Track your fleet in real-time',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 010 16c-4.5 0-8-7-8-8a8 8 0 0116 0z"/></svg>,
              },
              {
                title: lang === 'ar' ? 'الفرق والمحاور' : 'Teams & Hubs',
                desc:  lang === 'ar' ? 'إدارة السائقين عبر متاجر متعددة' : 'Manage riders across multiple stores',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><circle cx="9" cy="7" r="3"/><circle cx="17" cy="9" r="2"/><path d="M2 21v-1a7 7 0 0114 0v1"/><path d="M17 11a5 5 0 015 5v1"/></svg>,
              },
              {
                title: lang === 'ar' ? 'كشف الاحتيال بالذكاء الاصطناعي' : 'AI Fraud Detection & Route Validation',
                desc:  lang === 'ar' ? 'كشف الاحتيال قبل أن يكلفك' : 'Detect fraud before it costs you',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7z"/><path d="M9 12l2 2 4-4"/></svg>,
              },
              {
                title: lang === 'ar' ? 'تتبع العميل بالرسائل' : 'Customer SMS Live Tracking',
                desc:  lang === 'ar' ? 'مشاركة التتبع اللحظي مع العميل' : 'Share real-time delivery tracking',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg>,
              },
              {
                title: lang === 'ar' ? 'حلول الدفع الإلكتروني' : 'Cashless Payment Solutions',
                desc:  lang === 'ar' ? 'اقبل الدفع بالبطاقة عند التوصيل' : 'Accept card on delivery payments',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
                highlight: true,
              },
              {
                title: lang === 'ar' ? 'إدارة النقد وعهدة السائق' : 'Cash Management & Rider Custody',
                desc:  lang === 'ar' ? 'تحكم في حدود الدفع النقدي للسائق' : 'Control rider COD limits',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="2" y="6" width="20" height="14" rx="2"/><path d="M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><circle cx="12" cy="13" r="2"/></svg>,
              },
              {
                title: lang === 'ar' ? 'تخطيط الوردية والحضور' : 'Shift Planning & Attendance',
                desc:  lang === 'ar' ? 'خطط الورديات وأتمتها' : 'Plan & automate rider shifts',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
              },
              {
                title: lang === 'ar' ? 'رواتب السائقين' : 'Rider Payroll',
                desc:  lang === 'ar' ? 'اربط الأداء بالرواتب التلقائية' : 'Link performance to automated payroll',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
              },
              {
                title: lang === 'ar' ? 'البيانات والتحليلات' : 'Data & Analytics',
                desc:  lang === 'ar' ? 'اكتشف الاختناقات الخفية' : 'Discover hidden bottlenecks',
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
              },
            ].map((f, i) => (
              <div className={`pf-item${f.highlight ? ' pf-item--highlight' : ''}`} key={i}>
                <div className="pf-item__icon">{f.icon}</div>
                <div>
                  <strong>{f.title}</strong>
                  <span>{f.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SYSTEM SCREENSHOTS ── */}
      <section className="system-screens" ref={screenRef}>
        <div className="container">
          <p className="section-label">{lang === 'ar' ? 'النظام في العمل' : 'The System in Action'}</p>
          <h2 className="section-title">{lang === 'ar' ? 'كل ما تحتاجه في لوحة واحدة' : 'Everything You Need, One Dashboard'}</h2>
          <div className={`screens-grid${screenVisible ? ' fade-up' : ''}`}>

            {/* Dashboard KPI screen */}
            <div className="screen-card screen-card--wide">
              <div className="screen-card__label">{lang === 'ar' ? 'لوحة التحكم' : 'Live Dashboard'}</div>
              <div className="screen-mock dashboard-screen">
                <div className="sm-header">
                  <div className="sm-title">Route Rover</div>
                  <div className="sm-date">{lang === 'ar' ? 'اليوم' : 'Today'}</div>
                </div>
                <div className="sm-kpis">
                  {[
                    { v: '138', l: lang === 'ar' ? 'طلبات مُسلَّمة' : 'Delivered', c: '#22C55E' },
                    { v: '14',  l: lang === 'ar' ? 'متاجر نشطة' : 'Active Stores', c: '#0057FF' },
                    { v: '39',  l: lang === 'ar' ? 'سائق' : 'Riders', c: '#00D4FF' },
                    { v: '52m', l: lang === 'ar' ? 'متوسط التوصيل' : 'Avg Delivery', c: '#F59E0B' },
                  ].map((k, i) => (
                    <div className="sm-kpi" key={i}>
                      <strong style={{ color: k.c }}>{k.v}</strong>
                      <span>{k.l}</span>
                    </div>
                  ))}
                </div>
                <div className="sm-chart-area">
                  <div className="sm-chart-label">{lang === 'ar' ? 'أداء التوصيل' : 'Delivery Performance'}</div>
                  <div className="sm-bars">
                    {[65, 80, 55, 90, 72, 88, 95, 60, 85, 78, 92, 70].map((h, i) => (
                      <div className="sm-bar" key={i} style={{ height: `${h}%`, background: i === 6 ? '#0057FF' : 'rgba(0,87,255,0.25)' }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Orders screen */}
            <div className="screen-card">
              <div className="screen-card__label">{lang === 'ar' ? 'الطلبات' : 'Orders'}</div>
              <div className="screen-mock orders-screen">
                <div className="sm-header">
                  <div className="sm-title">{lang === 'ar' ? 'قائمة الطلبات' : 'Order List'}</div>
                  <div className="sm-badge green">138</div>
                </div>
                <div className="sm-table">
                  {[
                    { id: 'ORD-1689A', store: lang === 'ar' ? 'متجر الرياض' : 'Riyadh Store', status: 'delivered', st: lang === 'ar' ? 'مُسلَّم' : 'Delivered' },
                    { id: 'ORD-1689B', store: lang === 'ar' ? 'متجر القاهرة' : 'Cairo Store',  status: 'transit',   st: lang === 'ar' ? 'في الطريق' : 'In Transit' },
                    { id: 'ORD-1689C', store: lang === 'ar' ? 'الفرع الرئيسي' : 'Main Branch', status: 'pending',   st: lang === 'ar' ? 'قيد الانتظار' : 'Pending' },
                    { id: 'ORD-1689D', store: lang === 'ar' ? 'متجر الإسكندرية' : 'Alex Store', status: 'delivered', st: lang === 'ar' ? 'مُسلَّم' : 'Delivered' },
                  ].map((o, i) => (
                    <div className="sm-row" key={i}>
                      <div className="sm-id">{o.id}</div>
                      <div className="sm-store">{o.store}</div>
                      <div className={`sm-status ${o.status}`}>{o.st}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Live Tracking screen */}
            <div className="screen-card">
              <div className="screen-card__label">{lang === 'ar' ? 'التتبع المباشر' : 'Live Tracking'}</div>
              <div className="screen-mock tracking-screen">
                <div className="sm-header">
                  <div className="sm-title">{lang === 'ar' ? 'الخريطة المباشرة' : 'Live Map'}</div>
                  <div className="sm-badge blue">39 {lang === 'ar' ? 'سائق' : 'riders'}</div>
                </div>
                <div className="sm-map">
                  <div className="map-grid" />
                  {[
                    { x: 30, y: 35, c: '#22C55E', l: 'A' },
                    { x: 55, y: 25, c: '#0057FF', l: 'B' },
                    { x: 70, y: 55, c: '#22C55E', l: 'C' },
                    { x: 20, y: 65, c: '#F59E0B', l: 'D' },
                    { x: 80, y: 30, c: '#22C55E', l: 'E' },
                    { x: 45, y: 70, c: '#EF4444', l: 'F' },
                  ].map((p, i) => (
                    <div key={i} className="map-pin" style={{ left: `${p.x}%`, top: `${p.y}%`, background: p.c }}>
                      {p.l}
                    </div>
                  ))}
                  <div className="map-legend">
                    <span className="legend-dot green" />{lang === 'ar' ? 'نشط' : 'Active'}
                    <span className="legend-dot blue" />{lang === 'ar' ? 'مُرسَل' : 'Dispatched'}
                    <span className="legend-dot red"  />{lang === 'ar' ? 'متأخر' : 'Late'}
                  </div>
                </div>
              </div>
            </div>

            {/* Riders screen */}
            <div className="screen-card">
              <div className="screen-card__label">{lang === 'ar' ? 'السائقون' : 'Riders'}</div>
              <div className="screen-mock riders-screen">
                <div className="sm-header">
                  <div className="sm-title">{lang === 'ar' ? 'أداء السائقين' : 'Rider Performance'}</div>
                </div>
                <div className="sm-riders">
                  {[
                    { name: lang === 'ar' ? 'أحمد محمد' : 'Ahmed M.', orders: 24, rating: 4.9, status: 'active' },
                    { name: lang === 'ar' ? 'خالد علي' : 'Khaled A.', orders: 19, rating: 4.7, status: 'active' },
                    { name: lang === 'ar' ? 'سامي حسن' : 'Sami H.', orders: 31, rating: 4.8, status: 'transit' },
                    { name: lang === 'ar' ? 'محمود يوسف' : 'Mahmoud Y.', orders: 15, rating: 4.5, status: 'idle' },
                  ].map((r, i) => (
                    <div className="sm-rider" key={i}>
                      <div className="sm-rider__avatar">{r.name.charAt(0)}</div>
                      <div className="sm-rider__info">
                        <strong>{r.name}</strong>
                        <span>{r.orders} {lang === 'ar' ? 'طلب' : 'orders'} · ★{r.rating}</span>
                      </div>
                      <div className={`sm-status ${r.status}`}>
                        {r.status === 'active' ? (lang === 'ar' ? 'نشط' : 'Active') :
                         r.status === 'transit' ? (lang === 'ar' ? 'في الطريق' : 'On Route') :
                         (lang === 'ar' ? 'خامل' : 'Idle')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="features" ref={featRef}>
        <div className="container">
          <p className="section-label">{t('features.label')}</p>
          <h2 className="section-title">{t('features.title')}</h2>
          <div className="features__grid">
            {FEATURES.map((f, i) => (
              <div className={`feature-card${featVisible ? ' fade-up' : ''}`}
                key={i} style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                <div className="feature-card__icon"><f.Icon /></div>
                <h3>{t(f.titleKey)}</h3>
                <p>{t(f.descKey)}</p>
                <Link to="/contact" className="feature-card__link">{t('features.link')}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="stats" ref={statsRef}>
        <div className="container">
          {STATS.map((s, i) => (
            <div className="stat" key={i}>
              <div className="stat__value">
                {s.suffix === '.9%'
                  ? <><CountUp end={s.value} />{s.suffix}</>
                  : <><span>-</span><CountUp end={s.value} />{s.suffix}</>
                }
              </div>
              <div className="stat__label">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── BLINDSPOT SECTION ── */}
      <section className="blindspot">
        <div className="container blindspot__inner">
          <div className="blindspot__text">
            <p className="section-label">{t('blindspot.label')}</p>
            <h2>{t('blindspot.title')}</h2>
            <p>{t('blindspot.body')}</p>
            <div className="blindspot__actions">
              <Link to="/contact" className="cta-primary">{t('blindspot.cta1')}</Link>
              <Link to="/features" className="cta-ghost">{t('blindspot.cta2')}</Link>
            </div>
          </div>
          <div className="blindspot__visual">
            <div className="dashboard-mock">
              <div className="mock-header">
                <span className="mock-dot red" /><span className="mock-dot yellow" /><span className="mock-dot green" />
                <span style={{ marginLeft: 8, fontSize: 12, color: '#5B7BA8' }}>{t('blindspot.mock.title')}</span>
              </div>
              <div className="mock-stats">
                {[
                  ['847', t('blindspot.mock.orders')],
                  ['94%', t('blindspot.mock.ontime')],
                  ['12m', t('blindspot.mock.avg')],
                  ['32',  t('blindspot.mock.drivers')],
                ].map(([v,l],i)=>(
                  <div className="mock-stat" key={i}>
                    <strong>{v}</strong><span>{l}</span>
                  </div>
                ))}
              </div>
              <div className="mock-bars">
                {[85,60,92,45,78,95,70].map((h,i)=>(
                  <div className="mock-bar" key={i} style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      {testimonials.length > 0 && (
        <section className="testimonials">
          <div className="container">
            <p className="section-label">{t('test.label')}</p>
            <h2 className="section-title">{t('test.title')}</h2>
            <div className="testimonials__grid">
              {testimonials.map(t => (
                <div className="testimonial-card" key={t.id}>
                  <div className="testimonial-card__stars">{'★'.repeat(t.rating)}</div>
                  <p className="testimonial-card__quote">"{t.quote}"</p>
                  <div className="testimonial-card__author">
                    <div className="testimonial-card__avatar">{t.name.charAt(0)}</div>
                    <div>
                      <strong>{t.name}</strong>
                      <span>{t.title}, {t.company}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA BANNER ── */}
      <section className="cta-banner">
        <div className="container">
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.sub')}</p>
          <Link to="/contact" className="cta-primary cta-primary--large">{t('cta.btn')}</Link>
        </div>
      </section>

    </main>
  );
}
